import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Button, PageHeader, Form } from 'antd';
import { generatePath, RouteComponentProps } from 'react-router';

import * as S from './EditCulture.styles';
import { useCulturesSlice } from '../../slice';
import { RouteConstants } from 'app/routes';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import {
  selectCultureDetails,
  selectCreateCompanyLoading,
} from '../../slice/selectors';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { IHarvestObj } from '../../slice/types';
import { MapModeEnum } from 'app/containers/Map/slice/types';
import { LEFT_COL_WIDTH } from 'utils/constants';
import CultureDetailsCard from '../../parts/CultureDetailsCard/CultureDetailsCard';
import { useMapSlice } from 'app/containers/Map/slice';
import {
  selectCurrentCulturePolygonsIds,
  selectSelectedFieldsByCompany,
  selectSelectedFieldsTotalAreaWithUnits,
} from 'app/containers/Map/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import CreateOrEditCompaniesList from '../../parts/CreateOrEditCompaniesList/CreateOrEditCompaniesList';

interface IReactRouterParams {
  cultureId: string;
  companyId?: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const EDIT_CULTURE_FORM_ID = 'editCultureForm';

const EditCulture = ({
  history,
  match: {
    params: { cultureId: cultureIdParam, companyId: companyIdParam },
  },
}: IProps) => {
  const { t } = useTranslation();

  const dispatch = useDispatch();

  const { actions } = useCulturesSlice();
  const mapActions = useMapSlice().actions;

  const [form] = Form.useForm();

  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const { loading, companyId, culturesArr } = useSelector(selectCultureDetails);
  const culturePolygonsIds = useSelector(selectCurrentCulturePolygonsIds);
  const selectedFieldsByCompany = useSelector(selectSelectedFieldsByCompany);
  const createLoading = useSelector(selectCreateCompanyLoading);
  const area = useSelector(selectSelectedFieldsTotalAreaWithUnits);

  useEffect(() => {
    if (envId && seasonId) {
      dispatch(
        actions.getCultureDetails({
          hiikoEnvironmentId: envId,
          cultureId: Number(cultureIdParam),
          companyId: companyIdParam ? Number(companyIdParam) : undefined,
          seasonId,
        }),
      );

      // Load all polygons on map
      dispatch(
        mapActions.onLoad({
          envId,
          seasonId,
          companyId: companyIdParam ? Number(companyIdParam) : null,
          companyGroupId: null,
        }),
      );
    }
  }, [
    envId,
    seasonId,
    mapActions,
    cultureIdParam,
    companyIdParam,
    actions,
    dispatch,
  ]);

  useEffect(() => {
    if (culturePolygonsIds.length) {
      dispatch(mapActions.setSelectedPolygonsIds(culturePolygonsIds));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [culturePolygonsIds.length, mapActions, dispatch]);

  useEffect(() => {
    if (culturesArr.length) {
      const updatesObj: IHarvestObj = {};
      culturesArr.forEach(cultureObj => {
        updatesObj[cultureObj.company.id] = cultureObj.harvest;
      });

      form.setFieldsValue({ ...updatesObj });
    }
  }, [culturesArr, form]);

  const pageTitle = useMemo(() => {
    let dynamicTitlePart = '';

    if (culturesArr.length) {
      dynamicTitlePart = companyId
        ? culturesArr[0].company.name
        : `${culturesArr[0].culture.name}`;
    }
    return t('Cultures.CreateCulture.EditCulture', {
      defaultValue: 'Edit - {{cultureOrCompany}}',
      cultureOrCompany: dynamicTitlePart,
    });
  }, [culturesArr, companyId, t]);

  const saveButtonHandler = (values: { [key: number]: string }) => {
    const cultureId = culturesArr[0]?.culture.id;
    const harvests: IHarvestObj = {};

    for (const [key, value] of Object.entries(values)) {
      harvests[Number(key)] = Number(value);
    }

    if (envId && cultureId) {
      dispatch(
        actions.createOrEditCulture({
          isEdit: true,
          hiikoEnvironmentId: envId,
          cultureId,
          companyId,
          selectedFields: selectedFieldsByCompany,
          harvests,
        }),
      );
    }
  };

  const onBackClick = () => {
    const cultureId = culturesArr[0].culture.id;

    if (envId && cultureId) {
      let url = generatePath(RouteConstants.cultures.details, {
        envId,
        cultureId,
      });

      if (companyId) {
        url = generatePath(RouteConstants.cultures.detailsByCompany, {
          envId,
          cultureId,
          companyId,
        });
      }

      history.push(url);
    }
  };

  return (
    <>
      <PageHeader
        onBack={onBackClick}
        className="page-header"
        title={pageTitle}
        extra={[
          <Button
            key="1"
            type="primary"
            form={EDIT_CULTURE_FORM_ID}
            htmlType="submit"
          >
            {t('Cultures.Details.Save', 'Save')}
          </Button>,
        ]}
      />
      {loading || createLoading ? (
        <S.ContentWrapper>
          <S.Spinner tip="Loading..." size="large" />
        </S.ContentWrapper>
      ) : (
        <S.ContentWrapper>
          <S.LeftCol flex={LEFT_COL_WIDTH}>
            <CultureDetailsCard
              cultureName={culturesArr[0]?.culture.name}
              sowingStart={null}
              area={area}
              sowingEnd={null}
            />
            <Form
              layout="vertical"
              form={form}
              initialValues={{ layout: 'vertical' }}
              requiredMark="optional"
              onFinish={saveButtonHandler}
              id={EDIT_CULTURE_FORM_ID}
            >
              <CreateOrEditCompaniesList />
            </Form>
          </S.LeftCol>
          <S.MapCol flex="auto">
            <MapCard
              mode={MapModeEnum.view}
              readOnly={false}
              showLayerMenu
              showCulturesMenu
            />
          </S.MapCol>
        </S.ContentWrapper>
      )}
    </>
  );
};

export default EditCulture;
